import React, { useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  useToast,
  Text,
  useColorModeValue,
  Img,
} from '@chakra-ui/react';

import ugxLogo from 'assets/png/logo-min.png';
import { useNavigate } from 'react-router-dom';
import { MdOutlineRemoveRedEye } from 'react-icons/md';
import { RiEyeCloseLine } from 'react-icons/ri';
import Card from '../../../components/card/Card';
import Cookies from 'js-cookie';
import { useState } from 'react';

import axiosInstance from 'api/axiosInstance';
import apiUrls from 'api/apiUrls';
import { jwtDecode } from 'jwt-decode';
const PLATFORM_ID = process.env.REACT_APP_PLATFORM_ID;

function SignIn() {
  const [data, setData] = React.useState({ email: '', password: '' });
  const [show, setShow] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [userAuthority, setUserAuthority] = useState(null);
  const [customerId, setCustomerId] = useState(null);

  const navigate = useNavigate();
  const toast = useToast();

  const onChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
  };

  const handleClick = () => setShow(!show);

  const handleAuth = (e) => {
    e.preventDefault();
    handleLogin({
      username: data.email,
      password: data.password,
    });
  };

  const handleLogin = async (loginData) => {
    setIsLoading(true);

    try {
      const response = await axiosInstance.post(apiUrls.login, loginData);
      const result = response.data;

      // Store tokens
      Cookies.set('authToken', result.token, {
        secure: false,
        sameSite: 'strict',
      });

      Cookies.set('refreshToken', result.refreshToken, {
        secure: false,
        sameSite: 'strict',
      });

      // Decode token
      const decoded = jwtDecode(result.token);
      const userId = decoded.userId;

      // IMPORTANT: call only ONCE with userId
      await fetchUserAuthority(userId);

      // Get platform access token
      const resp = await axiosInstance.post(apiUrls.getAccessToken, {
        platform_id: PLATFORM_ID,
      });

      Cookies.set('UGXAuthorization', resp.data.access_token, {
        secure: false,
        sameSite: 'strict',
      });

      toast({
        title: 'Login successful',
        description: 'You have successfully logged in.',
        status: 'success',
        duration: 3000,
        position: 'top',
        isClosable: true,
      });

      navigate('/dashboard', { replace: true });
    } catch (error) {
      const { status, message } = error?.response?.data || {};

      toast({
        title: status === 401 ? 'Invalid credentials' : 'Login error',
        description:
          message ||
          (status === 401
            ? 'Please check your email and password.'
            : 'Something went wrong. Try again later.'),
        status: 'error',
        duration: 3000,
        position: 'top',
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUserAuthority = async (userId) => {
    if (!userId) {
      console.warn('fetchUserAuthority called without userId');
      return;
    }

    try {
      const response = await axiosInstance.get(
        apiUrls.fetchUserDetails(userId),
      );

      const data = response.data;

      const authority = 'TENANT_ADMIN';
      const isAdmin = data?.additionalInfo?.isAdmin;
      const customerId = data?.customerId?.id;

      const finalAuthority = isAdmin === false ? 'TENANT_CUSTOMER' : authority;

      setUserAuthority(finalAuthority);
      setCustomerId(customerId);

      localStorage.setItem('userAuthority', finalAuthority);
      localStorage.setItem('customerId', customerId);
    } catch (error) {
      console.error('Error fetching user authority:', error);
      setUserAuthority(null);
    }
  };
  const handleEnterKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAuth(e);
    }
  };

  // Theme
  const textColor = useColorModeValue('navy.800', 'white');
  const textColorSecondary = useColorModeValue('gray.500', 'navy.400');
  const brandStars = useColorModeValue('brand.500', 'brand.400');
  const inputFocusBorder = useColorModeValue('brand.500', 'brand.300');
  const cardBg = useColorModeValue('white', 'navy.700');
  const cardShadow = useColorModeValue(
    '0 12px 32px rgba(0, 0, 0, 0.12)',
    '0 12px 32px rgba(0, 0, 0, 0.6)',
  );
  const buttonHoverBg = useColorModeValue('brand.600', 'brand.300');

  return (
    <Flex
      h="100vh"
      w="100%"
      align="center"
      justify="center"
      direction="column" // ✅ stack vertically
      px={{ base: '4', md: '12' }}
      bg="gray.50"
    >
      {/* LOGO */}
      <Img src={ugxLogo} alt="UGX Logo" w="140px" mb="2" />

      {/* CONFIGURATOR */}
      <Text
        fontSize="md"
        fontWeight="600"
        letterSpacing="3px"
        // color={textColorSecondary}
        mb="6"
        mt={3}
      >
        CONFIGURATOR
      </Text>

      {/* CARD */}
      <Card
        bg={cardBg}
        boxShadow={cardShadow}
        w={{ base: '100%', md: '600px' }}
        p={{ base: 8, md: 12 }}
        borderRadius="20px"
      >
        <Box mb={10}>
          <Heading
            color={textColor}
            fontSize={{ base: '2xl', md: '3xl' }}
            mb="3"
          >
            Sign In
          </Heading>
          <Text color={textColorSecondary} fontSize="lg">
            Sign in to access your dashboard
          </Text>
        </Box>

        {/* Email */}
        <FormControl isRequired mb="6">
          <FormLabel fontWeight="600" fontSize="md" color={textColor}>
            Email{' '}
            <Text as="span" color={brandStars}>
              *
            </Text>
          </FormLabel>
          <Input
            name="email"
            type="email"
            size="lg"
            placeholder="you@example.com"
            value={data.email}
            onChange={onChange}
            autoComplete="email"
            _focus={{ borderColor: inputFocusBorder }}
            onKeyDown={handleEnterKeyPress}
          />
        </FormControl>

        {/* Password */}
        <FormControl isRequired mb="6">
          <FormLabel fontWeight="600" fontSize="md" color={textColor}>
            Password{' '}
            <Text as="span" color={brandStars}>
              *
            </Text>
          </FormLabel>
          <InputGroup size="lg">
            <Input
              name="password"
              type={show ? 'text' : 'password'}
              placeholder="********"
              value={data.password}
              onChange={onChange}
              autoComplete="current-password"
              _focus={{ borderColor: inputFocusBorder }}
              onKeyDown={handleEnterKeyPress}
            />
            <InputRightElement>
              <Icon
                as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                onClick={handleClick}
                cursor="pointer"
                color={textColorSecondary}
              />
            </InputRightElement>
          </InputGroup>
        </FormControl>

        {/* BUTTON */}
        <Button
          w="100%"
          size="lg"
          fontWeight="700"
          color="white"
          bg="brand.500"
          _hover={{ bg: buttonHoverBg }}
          onClick={handleAuth}
          isLoading={isLoading}
          loadingText="Signing in..."
        >
          Sign In
        </Button>
      </Card>
    </Flex>
  );
}

export default SignIn;
